import Decimal from 'decimal.js';

import { AssetV2IntegrationType } from '@/types/schema';
import { PathsOf } from '@/types/subform';

import { EntityType } from '../types/EntityType';

// `''` = unset, `'g:<grantorId>'` = grantor residency, `'s:<stateCode>'` = fixed state.
// String encoding lets the union flow through a single FormAwareSelectInput.
export type AssetTaxSitusFormValue = string;
export const TAX_SITUS_GRANTOR_PREFIX = 'g:';
export const TAX_SITUS_STATE_PREFIX = 's:';

export const STRUCTURED_ASSETS_SUBFORM_NAMESPACE =
  'structuredAssetsSubform' as const;
export type NamespaceType = typeof STRUCTURED_ASSETS_SUBFORM_NAMESPACE;
export const VARIANTS = ['default'] as const;
export type VariantType = (typeof VARIANTS)[number];

export interface StructuredAsset {
  categoryId: string;
  title: string;
  // null for freshly added rows until the user enters a number.
  value: Decimal | null;
  integrationType: AssetV2IntegrationType | null;
  // The AssetV2 id of this row in the current (form-loaded) valuation, when
  // it exists. New rows added in the form leave this undefined. Used at
  // submit time to drive `updateValuations.updateAssets[].updateAssetStatic`
  // for situs-only edits on existing assets.
  assetID?: string | null;
  assetStaticID?: string | null;
  // The display name from the previous valuation associated with the assetStaticID.
  // Used to detect renames — if the current title differs, we should not
  // carry forward the assetStaticID (the user semantically replaced the asset).
  assetStaticDisplayName?: string | null;
  taxSitus?: AssetTaxSitusFormValue;
  // Snapshot at form-load time so submit can detect a real situs change
  // (not just "user opened the row, didn't touch the dropdown").
  _originalTaxSitus?: AssetTaxSitusFormValue;
}

export interface StructuredAssetsSubformProperties {
  accountId?: string;
  valuationId?: string;
  description: string;
  assets: StructuredAsset[];
  documentIds?: string[];
  // this is a computed value, not a field in the subform
  _assetsValueSum: Decimal;
}

// this is what the subform's values will be nested under in the parent form
export interface Fields {
  [STRUCTURED_ASSETS_SUBFORM_NAMESPACE]?: StructuredAssetsSubformProperties;
}

export type SubformField = PathsOf<Fields>;

// this is Props for the UI component
export interface Props {
  householdId?: string;
  entityType: EntityType;
  entityId?: string;
  disabled?: boolean;
  hideDescriptionInput?: boolean;
  // Household client-profile IDs of the entity's principals (owner/grantor/donor).
  // Used to seed the owner-aware default tax situs: a sole-principal entity
  // defaults to that owner's residency; joint/multi falls back to primary grantor.
  ownerPrincipalIds?: string[];
}

export const defaultValues: StructuredAssetsSubformProperties = {
  accountId: '',
  valuationId: '',
  description: '',
  assets: [],
  _assetsValueSum: new Decimal(0),
  documentIds: [],
};
